import qs, {IParseOptions, IStringifyOptions} from 'qs';
import * as R from 'ramda';
import {compile} from 'path-to-regexp';
import {isNilOrEmpty} from './DataUtils';

export function urlJoin(...urlPieces: string[] | [string[]]) {
  let strArray: string[] = [];

  if (Array.isArray(urlPieces[0])) {
    strArray = urlPieces[0];
  } else {
    strArray = urlPieces as string[];
  }

  const resultArray = [];
  if (strArray.length === 0) {
    return '';
  }

  // If the first part is a plain protocol, we combine it with the next part.
  if (strArray[0].match(/^[^/:]+:\/*$/) && strArray.length > 1) {
    const first = strArray.shift();
    strArray[0] = first + strArray[0];
  }

  // There must be two or three slashes in the file protocol, two slashes in anything else.
  if (strArray[0].match(/^file:\/\/\//)) {
    strArray[0] = strArray[0].replace(/^([^/:]+):\/*/, '$1:///');
  } else {
    strArray[0] = strArray[0].replace(/^([^/:]+):\/*/, '$1://');
  }

  for (let i = 0; i < strArray.length; i++) {
    let component = strArray[i];

    if (component === '') {
      continue;
    }

    if (i > 0) {
      // Removing the starting slashes for each component but the first.
      component = component.replace(/^[\/]+/, '');
    }
    if (i < strArray.length - 1) {
      // Removing the ending slashes for each component but the last.
      component = component.replace(/[\/]+$/, '');
    } else {
      // For the last component we will combine multiple slashes to a single one.
      component = component.replace(/[\/]+$/, '/');
    }

    resultArray.push(component);
  }

  let str = resultArray.join('/');
  // Each input component is now separated by a single slash except the possible first plain protocol part.

  // remove trailing slash before parameters or hash
  str = str.replace(/\/(\?|&|#[^!])/g, '$1');

  // replace ? in parameters with &
  const parts = str.split('?');
  str = parts.shift() + (parts.length > 0 ? '?' : '') + parts.join('&');

  return str;
}

export function addQueryParams(
  url: string,
  params: Record<string, unknown>,
  options?: {
    parseOptions?: IParseOptions;
    stringifyOptions?: IStringifyOptions;
  },
): string {
  const [base, existingQuery] = url.split('?');

  return R.pipe(
    // Parse the existing query params into an object
    R.partialRight(qs.parse, [options?.parseOptions]),
    // Merge existing query params with the new query params. Note: params will take precedence over the existing params.
    R.mergeLeft(params),
    // Convert query params object back into a string
    R.partialRight(qs.stringify, [options?.stringifyOptions]),
    // Join the base and the query params back into a fully-qualified URL
    R.pair(base),
    R.join('?'),
  )(existingQuery);
}

export const removeQueryParams = (url: string, params: string[]): string => {
  const [base, existingQuery] = url.split('?');
  const queryObject = qs.parse(existingQuery);
  const updatedQueryObject = R.omit(params, queryObject);
  const nextParams = Object.keys(updatedQueryObject);
  return nextParams.length === 0
    ? base
    : `${base}?${qs.stringify(updatedQueryObject)}`;
};

export const resolveRoute = R.curry(
  (route: string, data: Record<string, unknown>) =>
    compile(route, {encode: encodeURIComponent})(data),
);

export const replaceSubdomainInUrl = (
  initialUrl: string | URL,
  newSubdomain: string,
): URL => {
  const url = new URL(initialUrl);
  const [, domain, tld] = url.hostname.split('.');
  url.hostname = `${newSubdomain}.${domain}.${tld}`;
  return url;
};

type BuildUrlOptions = {
  arrayFormat?: 'indices' | 'brackets' | 'repeat' | 'comma';
};

/** Build a URL from a route and query params.  */
export function buildUrl({
  route,
  queryParams = {},
  fragment,
  arrayFormat = 'repeat',
}: {
  route: string;
  queryParams?: Record<string, string | unknown>;
  fragment?: string;
  arrayFormat?: BuildUrlOptions['arrayFormat'];
}): string {
  const filteredParams = Object.fromEntries(
    Object.entries(queryParams).filter(([, value]) => !isNilOrEmpty(value)),
  );

  const queryString = qs.stringify(filteredParams, {
    arrayFormat,
    encode: true,
  });

  const hash = fragment ? `#${encodeURIComponent(fragment)}` : '';

  return [route, queryString ? `?${queryString}` : '', hash].join('');
}
